/**
 * Extracts the orientation from EXIF data in a JPEG image
 * @param file The image file to extract orientation from
 * @returns A promise that resolves to the orientation value (1-8)
 */
export async function getImageOrientation(file: File): Promise<number> {
    // Default orientation (no rotation)
    const DEFAULT_ORIENTATION = 1
  
    // Only process jpeg files which might contain EXIF
    if (!file.type.match(/jpe?g/i)) {
      return DEFAULT_ORIENTATION
    }
  
    return new Promise((resolve) => {
      const reader = new FileReader()
  
      reader.onload = (event) => {
        try {
          if (!event.target?.result) {
            resolve(DEFAULT_ORIENTATION)
            return
          }
  
          const view = new DataView(event.target.result as ArrayBuffer)
  
          // Check for valid JPEG header
          if (view.getUint16(0, false) !== 0xffd8) {
            resolve(DEFAULT_ORIENTATION)
            return
          }
  
          const length = view.byteLength
          let offset = 2
  
          while (offset < length - 2) { // Ensure we have at least 2 bytes to read
            // Check if we have enough bytes to read the marker
            if (offset + 2 > length) {
              break
            }
            
            const marker = view.getUint16(offset, false)
            offset += 2
  
            // EXIF APP1 marker (0xFFE1)
            if (marker === 0xffe1) {
              // Make sure we have enough bytes to check for EXIF header
              if (offset + 6 > length) {
                break
              }
              
              // Check for EXIF header
              if (view.getUint32(offset + 2, false) !== 0x45786966) {
                resolve(DEFAULT_ORIENTATION)
                return
              }
  
              // Make sure we have enough bytes to read the byte alignment
              if (offset + 10 > length) {
                break
              }
              
              const little = view.getUint16(offset + 8, false) === 0x4949
              offset += 8
  
              // Make sure we have enough bytes to read the number of tags
              if (offset + 2 > length) {
                break
              }
              
              // Find the orientation tag (0x0112)
              const tags = view.getUint16(offset, little)
              offset += 2
  
              // Make sure we don't try to read too many tags
              const maxTags = Math.min(tags, 128) // Limit to a reasonable number
              
              for (let i = 0; i < maxTags; i++) {
                // Make sure we have enough bytes to read the tag
                if (offset + (i + 1) * 12 > length) {
                  break
                }
                
                if (view.getUint16(offset + i * 12, little) === 0x0112) {
                  // Make sure we have enough bytes to read the orientation value
                  if (offset + i * 12 + 8 + 2 > length) {
                    break
                  }
                  
                  // Return the orientation value
                  const orientation = view.getUint16(offset + i * 12 + 8, little)
                  resolve(orientation)
                  return
                }
              }
            } else if ((marker & 0xff00) !== 0xff00) {
              break
            } else {
              // Make sure we have enough bytes to read the segment length
              if (offset + 2 > length) {
                break
              }
              
              const segmentLength = view.getUint16(offset, false)
              
              // Check if the segment length is valid
              if (segmentLength < 2 || offset + segmentLength > length) {
                break
              }
              
              offset += segmentLength
            }
          }
  
          resolve(DEFAULT_ORIENTATION)
        } catch (error) {
          console.error("Error reading EXIF orientation:", error)
          resolve(DEFAULT_ORIENTATION)
        }
      }
  
      reader.onerror = () => resolve(DEFAULT_ORIENTATION)
  
      // Read the file as ArrayBuffer to access binary data
      reader.readAsArrayBuffer(file.slice(0, 64 * 1024)) // Read only first 64KB for efficiency
    })
  }
  
  /**
   * Fixes the orientation of an image based on EXIF data
   * @param file The image file to fix orientation for
   * @param imageUrl The URL of the image to fix
   * @returns A promise that resolves to the corrected image URL
   */
  export async function fixImageOrientation(file: File, imageUrl: string): Promise<string> {
    // Only process jpeg files which might contain EXIF
    if (!file.type.match(/jpe?g/i)) {
      return imageUrl
    }
  
    try {
      // Create an image from the URL
      const img = new Image()
      img.crossOrigin = "anonymous"
  
      await new Promise((resolve, reject) => {
        img.onload = resolve
        img.onerror = reject
        img.src = imageUrl
      })
  
      // Get orientation from the file
      const orientation = await getImageOrientation(file)
  
      // If no rotation needed, return original URL
      if (orientation <= 1) {
        return imageUrl
      }
  
      // Create canvas with proper dimensions
      const canvas = document.createElement("canvas")
      const width = img.width
      const height = img.height
  
      // Adjust canvas dimensions for rotated orientations
      if ([5, 6, 7, 8].includes(orientation)) {
        canvas.width = height
        canvas.height = width
      } else {
        canvas.width = width
        canvas.height = height
      }
  
      const ctx = canvas.getContext("2d")
      if (!ctx) return imageUrl
  
      // Apply transformations based on orientation
      switch (orientation) {
        case 2: // horizontal flip
          ctx.translate(width, 0)
          ctx.scale(-1, 1)
          break
        case 3: // 180° rotate
          ctx.translate(width, height)
          ctx.rotate(Math.PI)
          break
        case 4: // vertical flip
          ctx.translate(0, height)
          ctx.scale(1, -1)
          break
        case 5: // vertical flip + 90° rotate
          ctx.rotate(0.5 * Math.PI)
          ctx.scale(1, -1)
          break
        case 6: // 90° rotate
          ctx.translate(0, 0)
          ctx.rotate(0.5 * Math.PI)
          ctx.translate(0, -height)
          break
        case 7: // horizontal flip + 90° rotate
          ctx.rotate(0.5 * Math.PI)
          ctx.translate(width, -height)
          ctx.scale(-1, 1)
          break
        case 8: // 270° rotate
          ctx.translate(0, width)
          ctx.rotate(-0.5 * Math.PI)
          break
      }
  
      // Draw the image with the applied transformations
      ctx.drawImage(img, 0, 0)
  
      // Convert canvas to data URL
      const correctedUrl = canvas.toDataURL(file.type)
  
      // Revoke the original object URL to prevent memory leaks
      URL.revokeObjectURL(imageUrl)
  
      return correctedUrl
    } catch (error) {
      console.error("Error fixing image orientation:", error)
      return imageUrl
    }
  }